/**
 * CardEditorPanel — Controlled sidebar for the product card editor.
 *
 * Receives template state from useCardEditorState (parent).
 * Renders: Card Style controls → section list (DnD) → Add Section dropdown.
 */

import { useState, useCallback } from 'react' // useState for expandedSection only
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CardStyleControls } from './CardStyleControls'
import { CardSectionListItem } from './CardSectionListItem'
import type {
  CardTemplate,
  CardSectionType,
  ContainerConfig,
} from './card-editor-types'
import {
  ALL_SECTION_TYPES,
  CARD_SECTION_META,
  DEFAULT_SECTION_CONFIGS,
} from './card-editor-types'

interface CardEditorPanelProps {
  /** Current template state (owned by useCardEditorState) */
  template: CardTemplate
  /** Called on every template change — updates parent state + PostMessage preview */
  onTemplateChange: (template: CardTemplate) => void
}

export function CardEditorPanel({ template, onTemplateChange }: CardEditorPanelProps) {
  const [expandedSection, setExpandedSection] = useState<CardSectionType | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // ── Helpers ──────────────────────────────────────────

  const updateTemplate = useCallback(
    (next: CardTemplate) => {
      onTemplateChange(next)
    },
    [onTemplateChange],
  )

  // ── Container config handlers ────────────────────────

  const handleContainerConfigChange = useCallback(
    (key: keyof ContainerConfig, value: string) => {
      updateTemplate({
        ...template,
        containerConfig: { ...template.containerConfig, [key]: value },
      })
    },
    [template, updateTemplate],
  )

  // ── Section reorder ──────────────────────────────────

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = template.sections.findIndex((s) => s.type === active.id)
      const newIndex = template.sections.findIndex((s) => s.type === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      updateTemplate({
        ...template,
        sections: arrayMove(template.sections, oldIndex, newIndex),
      })
    },
    [template, updateTemplate],
  )

  // ── Section remove ───────────────────────────────────

  const handleRemove = useCallback(
    (type: CardSectionType) => {
      if (expandedSection === type) setExpandedSection(null)
      updateTemplate({
        ...template,
        sections: template.sections.filter((s) => s.type !== type),
      })
    },
    [template, expandedSection, updateTemplate],
  )

  // ── Section add ──────────────────────────────────────

  const handleAdd = useCallback(
    (type: CardSectionType) => {
      updateTemplate({
        ...template,
        sections: [
          ...template.sections,
          { type, config: { ...DEFAULT_SECTION_CONFIGS[type] } },
        ],
      })
    },
    [template, updateTemplate],
  )

  // ── Section config change ────────────────────────────

  const handleSectionConfigChange = useCallback(
    (type: CardSectionType, key: string, value: string) => {
      updateTemplate({
        ...template,
        sections: template.sections.map((s) =>
          s.type === type ? { ...s, config: { ...s.config, [key]: value } } : s,
        ),
      })
    },
    [template, updateTemplate],
  )

  // ── Expand/collapse ──────────────────────────────────

  const toggleExpand = useCallback(
    (type: CardSectionType) => {
      setExpandedSection((prev) => (prev === type ? null : type))
    },
    [],
  )

  // ── Available sections for "Add" dropdown ────────────

  const activeSectionTypes = new Set(template.sections.map((s) => s.type))
  const availableSections = ALL_SECTION_TYPES.filter((t) => !activeSectionTypes.has(t))

  return (
    <div className="h-full overflow-y-auto p-4 [scrollbar-width:none]">
      {/* Card Style controls */}
      <CardStyleControls
        config={template.containerConfig}
        onChange={handleContainerConfigChange}
      />

      {/* Section list */}
      <div className="mt-6">
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
          Card Sections
        </h3>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={template.sections.map((s) => s.type)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-1.5" role="list">
              {template.sections.map((section) => (
                <CardSectionListItem
                  key={section.type}
                  section={section}
                  isExpanded={expandedSection === section.type}
                  onToggleExpand={() => toggleExpand(section.type)}
                  onRemove={() => handleRemove(section.type)}
                  onConfigChange={(key, value) =>
                    handleSectionConfigChange(section.type, key, value)
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Empty state */}
        {template.sections.length === 0 && (
          <div className="rounded-sm border border-dashed border-gray-300 px-4 py-8 text-center">
            <p className="text-sm text-gray-400">No sections added yet.</p>
            <p className="mt-1 text-xs text-gray-400">
              Use the button below to add card sections.
            </p>
          </div>
        )}
      </div>

      {/* Add Section dropdown */}
      {availableSections.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-sm border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-500 transition-colors hover:border-sky-400 hover:text-sky-600">
              <Plus className="h-4 w-4" />
              Add Section
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            {availableSections.map((type) => {
              const meta = CARD_SECTION_META[type]
              const Icon = meta.icon
              return (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleAdd(type)}
                  className="flex items-center gap-2"
                >
                  <Icon className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-xs text-gray-400">{meta.description}</div>
                  </div>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
